import {Action, Module, Mutation} from 'vuex-module-decorators'
import {MdbsCore} from "@/api/tsclient";
import store, {alertModule} from "@/store";
import BaseModule from "@/store/modules/BaseModule";
import UserCreateDto = MdbsCore.UserCreateDto;
import UserUpdateDto = MdbsCore.UserUpdateDto;
import IUserUpdateDto = MdbsCore.IUserUpdateDto;
import IUserCreateDto = MdbsCore.IUserCreateDto;
import ShortUserDto = MdbsCore.ShortUserDto;
import UserPasswordResetDto = MdbsCore.UserPasswordResetDto;
import IUserCompleteDto = MdbsCore.IUserCompleteDto;
import UserCompleteDto = MdbsCore.UserCompleteDto;
import PersonDataDto = MdbsCore.PersonDataDto;
import UserPasswordChangeDto = MdbsCore.UserPasswordChangeDto;

const userClient = new MdbsCore.UserClient();

@Module({namespaced: true, name: "users", store: store})
export default class UsersModule extends BaseModule {
    _users: Array<ShortUserDto> = [];
    _currentCompleteUser: IUserCompleteDto = new UserCompleteDto();

    get users() {
        return this._users;
    }

    get currentCompleteUser(): IUserCompleteDto {
        return this._currentCompleteUser;
    }

    @Mutation
    setUsers(users: Array<ShortUserDto>) {
        // presort, since in most usecase we want a nicely sorted user list
        users.sort((u1, u2) => (u1.displayName ?? "").localeCompare(u2.displayName ?? ""))
        this._users.updateValues(users);
    }

    @Mutation
    setCurrentCompleteUser(user: UserCompleteDto) {
        this._currentCompleteUser = user;
    }

    @Mutation
    resetUsers() {
        this._users.clear();
    }

    @Mutation
    resetCurrentCompleteUser() {
        this._currentCompleteUser = new UserCompleteDto();
    }

    @Action({rawError: true})
    async loadAll() {
        this.startLoading();
        try {
            this.setUsers(await userClient.getAll(false));
        } catch (e) {
            await alertModule.handleException(e);
            this.resetUsers();
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async getCompleteUserById(userId: string) {
        await this.execute(async () => {
            const userCompleteDto = await userClient.findUserComplete(userId);

            this.setCurrentCompleteUser(userCompleteDto);
        });
    }

    @Action({rawError: true})
    async createCompleteUser(payload: { user: IUserCreateDto, deactivate: boolean }) {
        await this.execute(async () => {
            const userCreateDto = new UserCreateDto(payload.user);
            userCreateDto.personData = new PersonDataDto(payload.user.personData); // nswag issue

            const createdUser = await userClient.create(userCreateDto);

            if (payload.deactivate || false) {
                await userClient.deactivate(createdUser.id);
            }
            await this.loadAll();
        });
    }

    @Action({rawError: true})
    async updateCompleteUser(payload: { user: IUserUpdateDto, deactivate: boolean, newPassword?: string }) {
        await this.execute(async () => {
            const userUpdateDto = new UserUpdateDto(payload.user);
            userUpdateDto.personData = new PersonDataDto(payload.user.personData);

            await userClient.update(payload.user.id, userUpdateDto);
            const updatedUser = await userClient.findUser(payload.user.id);

            if (updatedUser.isDeactivated != payload.deactivate) {
                if (payload.deactivate) {
                    await userClient.deactivate(payload.user.id);
                } else {
                    await userClient.activate(payload.user.id);
                }
            }

            if (payload.newPassword) {
                const passwordDto = new UserPasswordChangeDto();
                passwordDto.id = payload.user.id;
                passwordDto.newPasswordString = payload.newPassword;

                await userClient.updatePassword(payload.user.id, passwordDto);
            }
            await this.loadAll();
        });
    }

    @Action({rawError: true})
    async deleteUser(userId: string) {
        await this.execute(async () => {
            await userClient.delete(userId);
            await this.loadAll();
        });
    }

    @Action({rawError: true})
    async deactivateUser(userId: string) {
        this.startLoading();
        try {
            await userClient.deactivate(userId)
            await this.loadAll()
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action
    async sendPasswordRecoveryMail(email: string) {
        this.startLoading();
        try {
            return await userClient.sendPasswordRecoveryEmail(email)
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action
    async resetPassword(resetPasswordDto: UserPasswordResetDto) {
        this.startLoading();
        try {
            return await userClient.resetPassword(resetPasswordDto);
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }
}
